import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCode, FaChartLine, FaHeadset } from 'react-icons/fa';
import SEO from '../components/SEO';
import { serviceSchema } from '../schemas/structuredData';
import './Services.css';

const Services = () => {
  const servicesDetail = [
    {
      icon: <FaCode />,
      title: 'Web Development',
      description: 'Create stunning, responsive websites that drive business growth. Our web development services include:',
      features: [
        'Custom website design and development',
        'E-commerce platforms',
        'Content Management Systems (CMS)',
        'Progressive Web Applications (PWA)',
        'Website maintenance and support'
      ]
    },
    {
      icon: <FaChartLine />,
      title: 'Digital Marketing',
      description: 'Grow your online presence and reach your target audience:',
      features: [
        'Search Engine Optimization (SEO)',
        'Social Media Marketing',
        'Pay-Per-Click (PPC) advertising',
        'Content marketing and strategy',
        'Email marketing campaigns'
      ]
    },
    {
      icon: <FaHeadset />,
      title: 'IT Consulting',
      description: 'Expert guidance for your technology strategy:',
      features: [
        'Technology strategy and planning',
        'Digital transformation consulting',
        'IT infrastructure assessment',
        'Software architecture design',
        'Project management'
      ]
    }
  ];

  return (
    <div className="services-page">
      <SEO 
        title="Our Services - Web Development, Digital Marketing & IT Consulting | HEGXIB Nigeria"
        description="Explore HEGXIB Nigeria's comprehensive digital services: custom web development, mobile apps, digital marketing, SEO, and IT consulting solutions for businesses."
        keywords="web development Nigeria, digital marketing services, IT consulting, mobile app development, SEO services, e-commerce development, CMS, software solutions"
        url="https://ng.hegxib.me/services"
        structuredData={serviceSchema}
      />
      {/* Page Header */}
      <motion.section 
        className="page-header"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="container">
          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Our Services
          </motion.h1>
          <motion.p
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            Comprehensive Digital Solutions for Your Business
          </motion.p>
        </div>
      </motion.section>

      {/* Services Detail */}
      <section className="services-detail">
        <div className="container">
          {servicesDetail.map((service, index) => (
            <motion.div
              key={index}
              className={`service-detail-item ${index % 2 === 1 ? 'reverse' : ''}`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <motion.div 
                className="service-detail-icon"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                {service.icon}
              </motion.div>
              
              <div className="service-detail-content">
                <h2>{service.title}</h2>
                <p>{service.description}</p>
                <ul>
                  {service.features.map((feature, idx) => (
                    <motion.li
                      key={idx}
                      initial={{ x: -20, opacity: 0 }}
                      whileInView={{ x: 0, opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.1 }}
                    >
                      {feature}
                    </motion.li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <motion.section 
        className="cta"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <div className="container">
          <motion.h2
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Let's Build Something Amazing Together
          </motion.h2>
          <motion.p
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            Contact us today to discuss your project requirements
          </motion.p>
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6, duration: 0.6 }}
          >
            <Link to="/contact" className="btn btn-primary">
              Get Started
            </Link>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default Services;
